import { toValue, computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { yardAssetKeys } from '@/composables/queryKeys';
import yardSpotAreaService from '@satellite/services/yardSpotArea-service';

import type { UseQueryOptions } from '@tanstack/vue-query';
import type { MaybeRefOrGetter } from 'vue';

type AreaId = string | null | undefined;
type WarehouseId = string | null | undefined;

export function useAreaById<TQueryFnData = unknown, TError = unknown, TData = TQueryFnData>(
  areaId: MaybeRefOrGetter<AreaId>,
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'> & {
    enabled?: MaybeRefOrGetter<boolean>;
  }
) {
  return useQuery<TQueryFnData, TError, TData>({
    ...options,
    queryKey: computed(() => ['yard-area', toValue(areaId)] as const),
    enabled: () => {
      const id = toValue(areaId);
      const externalEnabled = toValue(
        (options?.enabled as MaybeRefOrGetter<boolean> | undefined) ?? true
      );
      return externalEnabled && Boolean(id);
    },
    queryFn: () => {
      const id = toValue(areaId);
      if (!id) throw new Error('Missing areaId');
      return yardSpotAreaService.getOneById(id) as Promise<TQueryFnData>;
    }
  });
}

export function useSpotAreasByWarehouseId<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData
>(
  warehouseId: MaybeRefOrGetter<WarehouseId>,
  params?: MaybeRefOrGetter<Record<string, unknown>>,
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'> & {
    enabled?: MaybeRefOrGetter<boolean>;
  }
) {
  return useQuery<TQueryFnData, TError, TData>({
    ...options,
    queryKey: computed(() =>
      yardAssetKeys.spotAreas.byWarehouse(toValue(warehouseId), toValue(params))
    ),
    enabled: () => {
      const id = toValue(warehouseId);
      const externalEnabled = toValue(
        (options?.enabled as MaybeRefOrGetter<boolean> | undefined) ?? true
      );
      return externalEnabled && Boolean(id);
    },
    queryFn: async () => {
      const id = toValue(warehouseId);
      const queryParams = toValue(params) || {};
      if (!id) throw new Error('Missing warehouseId');
      return yardSpotAreaService.getManyByWarehouseId(id, queryParams) as Promise<TQueryFnData>;
    }
  });
}
